<template>
  <MinimalLayout
    :loading="user.isLoading || (!!user.authenticatedUser && step !== 'treezorSCA')"
    :title="
      step === 'forgotPassword' || step === 'forgotPasswordSuccess'
        ? t('login.forgotPasswordTitle')
        : t('login.title')
    "
  >
    <template v-if="step === 'login'">
      <v-form ref="loginForm" :disabled="loading" @submit.prevent="login">
        <v-text-field
          v-model.trim="email"
          :rules="[required, validEmail]"
          clearable
          :label="t('login.email')"
          class="mb-2"
          variant="outlined"
          autocomplete="email"
          data-testid="login-email-input"
        ></v-text-field>

        <v-text-field
          id="current-password"
          v-model="password"
          :rules="[required]"
          autocomplete="current-password"
          clearable
          class="mb-2"
          :label="t('login.password')"
          :type="password && showPass ? 'text' : 'password'"
          variant="outlined"
          :append-inner-icon="!password ? '' : showPass ? 'mdi-eye' : 'mdi-eye-off'"
          data-testid="login-password-input"
          @click:append-inner="() => (showPass = !showPass)"
        ></v-text-field>

        <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
          <span>{{ error }}</span>
          <span v-if="error === t('login.invalidCredentials')">
            <br />
            <a class="underline cursor-pointer" @click.prevent="step = 'forgotPassword'">
              {{ t("login.resetPassword") }}
            </a>
          </span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
          data-testid="login-submit-button"
        >
          {{ t("login.signIn") }}
        </v-btn>
      </v-form>

      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="step = 'forgotPassword'"
      >
        {{ t("login.forgotPassword") }}
      </v-btn>
    </template>

    <template v-if="step === 'forgotPassword'">
      <v-form ref="forgotPasswordForm" :disabled="loading" @submit.prevent="forgotPassword">
        <v-text-field
          v-model.trim="email"
          :rules="[required, validEmail]"
          clearable
          :label="t('login.email')"
          class="mb-2"
          variant="outlined"
        ></v-text-field>

        <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
          <span>{{ error }}</span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
        >
          {{ t("login.next") }}
        </v-btn>
      </v-form>
      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="step = 'login'"
      >
        {{ t("login.back") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'forgotPasswordSuccess'">
      <v-alert color="success" type="success">{{
        t("login.checkEmailForLink", { email })
      }}</v-alert>
      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="step = 'login'"
      >
        {{ t("login.back") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'mfa'">
      <div class="text-sm text-center mb-4">
        {{ t("login.verificationCodeSentTo", { phoneNumber: mfaPhoneNumber }) }}
      </div>
      <v-form ref="mfaForm" :disabled="loading" @submit.prevent="mfaSubmit">
        <v-text-field
          v-model.trim="mfaVerificationCode"
          :rules="[required]"
          autocomplete="one-time-code"
          clearable
          :label="t('login.verificationCodeLabel')"
          class="mb-2"
          variant="outlined"
          maxlength="6"
          inputmode="numeric"
          data-testid="login-mfa-code-input"
        ></v-text-field>

        <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
          <span>{{ error }}</span>
        </v-alert>

        <v-btn
          :disabled="loading"
          :loading="loading"
          block
          color="primary"
          size="large"
          type="submit"
          variant="flat"
        >
          {{ t("login.signIn") }}
        </v-btn>
      </v-form>
      <v-btn class="mt-3" :disabled="loading" block size="small" variant="text" @click="mfaBack">
        {{ t("login.back") }}
      </v-btn>
    </template>

    <template v-else-if="step === 'treezorSCA'">
      <div class="text-sm text-center mb-4">
        {{ t("login.treezorScaDescription") }}
      </div>

      <v-alert v-if="error" density="compact" class="mb-3" color="error" type="error">
        <span>{{ error }}</span>
      </v-alert>

      <v-btn
        :disabled="loading"
        :loading="loading"
        block
        color="primary"
        size="large"
        variant="flat"
        @click="treezorSCALogin"
      >
        {{ t("login.treezorScaSignIn") }}
      </v-btn>
      <v-btn
        class="mt-3"
        :disabled="loading"
        block
        size="small"
        variant="text"
        @click="treezorSCABack"
      >
        {{ t("login.back") }}
      </v-btn>
    </template>
  </MinimalLayout>
  <div ref="recaptchaContainer"></div>
</template>

<script lang="ts" setup>
import MinimalLayout from "@/components/MinimalLayout.vue";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useRedirect } from "@/router/redirect";
import { useUserStore } from "@/store";
import { useFirebaseErrorMessage } from "@/utils/errors";
import { verifyPhoneNumber } from "@/utils/recaptcha";
import { startAuthentication } from "@/utils/webAuthn";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import {
  AuthErrorCodes,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  type MultiFactorError,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
} from "firebase/auth";
import { nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useFirebaseAuth } from "vuefire";
import type { VForm } from "vuetify/components";

const { t } = useI18n();
const { currentLanguage } = useLanguage();
const user = useUserStore();
const auth = useFirebaseAuth();
const router = useRouter();
const route = useRoute();
const { getFirebaseErrorMessage } = useFirebaseErrorMessage();
const { required, validEmail } = useRules();
const { redirectToHome } = useRedirect();

const email = ref((route.query.email as string) || "");
const password = ref("");
const loading = ref(false);
const loginForm = ref<VForm>();
const forgotPasswordForm = ref<VForm>();
const showPass = ref(false);
const error = ref<null | string>(null);
const step = ref<"login" | "forgotPassword" | "forgotPasswordSuccess" | "mfa" | "treezorSCA">(
  "login",
);
const mfaForm = ref<VForm>();
const mfaVerificationCode = ref<string>("");
const mfaVerificationId = ref<string>("");
const mfaPhoneNumber = ref<string>("");
const mfaResolver = ref<MultiFactorResolver | null>(null);
const recaptchaContainer = ref<HTMLElement>();

// Compile-time gated: Vite strips this branch from production builds.
const demoAutoLoginEnabled =
  import.meta.env.DEV && import.meta.env.VITE_FIREBASE_PROJECT_ID === "demo-local";
const demoAutoLoginPending = ref(false);
const demoAutoLoginStarted = ref(false);

const startDemoAutoLogin = async () => {
  if (!demoAutoLoginEnabled || demoAutoLoginPending.value || loading.value) return;

  try {
    demoAutoLoginPending.value = true;
    const { demoLocalCredential } = await import("@/utils/demoAutoLogin");
    email.value = demoLocalCredential.email;
    password.value = demoLocalCredential.password;
    await nextTick();
    if (!loginForm.value) throw new Error("Login form is not ready");
    await login();
    // MFA keeps pending true so completeDemoAutoLoginMfa can run; clear otherwise.
    if (step.value !== "mfa") {
      demoAutoLoginPending.value = false;
    }
  } catch (cause) {
    demoAutoLoginPending.value = false;
    const wrappedError = new Error("Error in Login startDemoAutoLogin", { cause });
    console.error(wrappedError);
    error.value = t("login.error");
  }
};

const completeDemoAutoLoginMfa = async () => {
  if (!demoAutoLoginEnabled || !demoAutoLoginPending.value) return;

  try {
    const { fetchEmulatorVerificationCode } = await import("@/utils/demoAutoLogin");
    mfaVerificationCode.value = await fetchEmulatorVerificationCode();
    // Existing mfaVerificationCode watcher auto-submits when 6 digits are set.
  } catch (cause) {
    const wrappedError = new Error("Error in Login completeDemoAutoLoginMfa", { cause });
    console.error(wrappedError);
    error.value = t("login.error");
  } finally {
    demoAutoLoginPending.value = false;
  }
};

const login = async () => {
  error.value = null;
  try {
    console.log("Logging in...");
    if (!auth) throw new Error("Auth is not defined");
    const validateForm = await loginForm.value?.validate();
    if (!validateForm?.valid) return;

    loading.value = true;

    await signInWithEmailAndPassword(auth, email.value, password.value);
  } catch (cause) {
    if (cause instanceof FirebaseError && cause.code === AuthErrorCodes.MFA_REQUIRED) {
      await mfaSetup(cause as MultiFactorError);
      return;
    }

    loading.value = false;
    const wrappedError = new Error("Error in Login login function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("login.error");
    }
  }
};

const mfaSetup = async (multiFactorError: MultiFactorError) => {
  console.log("Handling multi-factor authentication");

  error.value = null;
  try {
    if (!auth) throw new Error("Auth is not defined");
    if (!recaptchaContainer.value) throw new Error("reCAPTCHA container is not mounted");

    mfaResolver.value = getMultiFactorResolver(auth, multiFactorError);
    const hint = mfaResolver.value.hints.find(
      (h): h is PhoneMultiFactorInfo => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    );
    if (!hint) {
      throw new Error("No multi-factor hint found for phone authentication");
    }
    mfaPhoneNumber.value = hint.phoneNumber;
    const phoneInfoOptions = {
      multiFactorHint: hint,
      session: mfaResolver.value.session,
    };

    console.log("Sending verification code to phone");
    mfaVerificationId.value = await verifyPhoneNumber(
      auth,
      recaptchaContainer.value,
      phoneInfoOptions,
    );

    step.value = "mfa";
    loading.value = false;
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Login mfaSetup function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("login.error");
    }
  }
};

const mfaSubmit = async () => {
  error.value = null;
  try {
    const validateForm = await mfaForm.value?.validate();
    if (!validateForm?.valid) return;
    if (!mfaResolver.value) throw new Error("Multi-factor resolver is not set");

    loading.value = true;
    console.log("Verifying code entered by user");
    const credential = PhoneAuthProvider.credential(
      mfaVerificationId.value,
      mfaVerificationCode.value,
    );
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    await mfaResolver.value.resolveSignIn(multiFactorAssertion);
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Login mfaSubmit function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("login.error");
    }
  }
};

const mfaBack = () => {
  step.value = "login";
  mfaResolver.value = null;
};

const treezorSCABack = async () => {
  await auth?.signOut();
  step.value = "login";
};

const treezorSCALogin = async () => {
  error.value = null;
  try {
    if (!auth) throw new Error("Auth is not defined");

    loading.value = true;
    console.log("Signing in with Treezor SCA...");

    const { webAuthnAuthenticationOptions } = await call("signInWithTreezorSCAPayload")({});
    const webAuthnAuthenticationResponse = await startAuthentication({
      optionsJSON: webAuthnAuthenticationOptions,
    });

    const { token } = await call("signInWithTreezorSCA")({ webAuthnAuthenticationResponse });

    await signInWithCustomToken(auth, token);
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Login treezorSCALogin function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("login.error");
    }
  }
};

const forgotPassword = async () => {
  error.value = null;
  try {
    const validateForm = await forgotPasswordForm.value?.validate();
    if (!validateForm?.valid) return;

    loading.value = true;
    console.log("Sending password reset email to", email.value);
    await call("sendUserPasswordResetEmail")({
      email: email.value,
      locale: currentLanguage.value.code,
    });

    step.value = "forgotPasswordSuccess";
    loading.value = false;
  } catch (cause) {
    loading.value = false;
    const wrappedError = new Error("Error in Login forgotPassword function", { cause });
    console.error(wrappedError);
    captureException(wrappedError);
    if (cause instanceof FirebaseError) {
      error.value = getFirebaseErrorMessage(cause);
    } else {
      error.value = t("login.error");
    }
  }
};

watch(
  user,
  (user) => {
    if (!user.authenticatedUser) return;

    // Users not enrolled in MFA are redirected to MFA setup by the router
    if (
      user.isKind("user") &&
      user.authenticatedUser.authClaims.isMfaEnrolled &&
      !user.authenticatedUser.authClaims.loggedInWithMfa
    ) {
      console.log("User is a Treezor user with expired access token, re-login with Treezor SCA");
      step.value = "treezorSCA";
      loading.value = false;
      return;
    }

    const redirect = router.currentRoute.value.query.redirect;
    if (typeof redirect === "string" && redirect) {
      void router.push(redirect);
      return;
    }

    void redirectToHome();
  },
  { immediate: true },
);

watch(step, () => {
  loginForm.value?.resetValidation();
  forgotPasswordForm.value?.resetValidation();
  mfaForm.value?.resetValidation();
  error.value = null;
  password.value = "";
  mfaVerificationCode.value = "";
});

watch(step, (newStep) => {
  if (newStep !== "mfa") return;
  if (demoAutoLoginPending.value) {
    void completeDemoAutoLoginMfa();
    return;
  }
  if (!import.meta.env.DEV || import.meta.env.VITE_DEV_MFA_AUTOFILL !== "true") return;
  const phoneNumber = mfaPhoneNumber.value.replaceAll(/\s/g, "");
  if (/^\+\*+3333$/.test(phoneNumber)) mfaVerificationCode.value = "333333";
  if (/^\+\*+6666$/.test(phoneNumber)) mfaVerificationCode.value = "666666";
});

watch(mfaVerificationCode, async (code) => {
  if (loading.value) return;
  if (/^\d{6}$/.test(code)) {
    await nextTick();
    void mfaSubmit();
  }
});

watch(
  [() => user.isLoading, () => user.authenticatedUser, recaptchaContainer],
  ([isLoading, authenticatedUser, container]) => {
    if (!demoAutoLoginEnabled || isLoading || authenticatedUser || !container) return;
    if (demoAutoLoginStarted.value || step.value !== "login") return;
    // Respect an explicit email prefill (e.g. password-reset deep link).
    if (route.query.email) return;
    demoAutoLoginStarted.value = true;
    void startDemoAutoLogin();
  },
  { immediate: true },
);
</script>

<style lang="css">
.grecaptcha-badge {
  visibility: hidden;
}
</style>
